import { defineStore } from 'pinia'
import { inject, ref } from 'vue'

import { DEFAULT_RUNTIME_CONFIG, type RuntimeConfig } from '@/domain/runtime'
import { RUNTIME_CONFIG_API_KEY } from '@/ports'

/**
 * useRuntimeConfigStore — petit store Pinia qui mémoïse le RuntimeConfig
 * fetché une fois au boot. PassthroughConfigCard et tout autre composant
 * qui a besoin de l'URL data-plane consomme via le getter `effectiveOrigin`
 * qui fallback sur window.location.origin si dataPlaneUrl est vide.
 */
export const useRuntimeConfigStore = defineStore('runtimeConfig', () => {
    const config = ref<RuntimeConfig>({ ...DEFAULT_RUNTIME_CONFIG })
    const loaded = ref(false)

    function setConfig(next: RuntimeConfig): void {
        config.value = { ...next }
        loaded.value = true
    }

    /**
     * effectiveOrigin retourne l'URL publique du data-plane à utiliser pour
     * les snippets BASE_URL — soit le `dataPlaneUrl` configuré, soit
     * `window.location.origin` (cas SaaS / self-hosted où data-plane et
     * control-plane sont derrière le même hostname).
     */
    function effectiveOrigin(): string {
        if (config.value.dataPlaneUrl) return config.value.dataPlaneUrl
        return window.location.origin
    }

    return { config, loaded, setConfig, effectiveOrigin }
})

/**
 * loadRuntimeConfig — helper à appeler une fois au bootstrap pour fetch le
 * config depuis l'API et hydrater le store. Best-effort : un échec n'empêche
 * pas le boot, le store reste sur DEFAULT_RUNTIME_CONFIG.
 */
export async function loadRuntimeConfig(): Promise<void> {
    const api = inject(RUNTIME_CONFIG_API_KEY, null)
    if (!api) return
    const store = useRuntimeConfigStore()
    try {
        const cfg = await api.get()
        store.setConfig(cfg)
    } catch {
        // Best-effort — le store garde le default.
    }
}
